import React, { useState, useEffect, useRef } from 'react'
import { Send, ArrowLeft, Bot, User } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useChatStore } from '../stores/chatStore'
import { useWheelStore } from '../stores/wheelStore'
import { useAuthStore } from '../stores/authStore'
import { useAIUsageStore } from '../stores/aiUsageStore'
import LoginPromptModal from '../components/LoginPromptModal'

const presetQuestions = [
  "What are the pros and cons of this decision?",
  "What risks and opportunities does this choice present?",
  "What alternatives should I consider for this decision?",
  "How can I evaluate the quality of this decision?",
  "How will this choice impact my long-term goals?",
  "What factors should I consider before making this decision?"
]

export const ChatPage: React.FC = () => {
  const location = useLocation()
  const { messages, isLoading, sendMessage, setDecision, clearMessages } = useChatStore()
  const { result } = useWheelStore()
  const [inputValue, setInputValue] = useState('')
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  const { user } = useAuthStore()
  const { canUseAI, incrementUsage, getRemainingUsage } = useAIUsageStore()
  
  // Get wheel result and context from route state
  const wheelResult = location.state?.wheelResult || result
  const context = location.state?.context
  


  useEffect(() => {
    if (wheelResult) {
      setDecision(wheelResult)
      // Clear previous chat messages when there's a new wheel result
      clearMessages()
    }
  }, [wheelResult, setDecision, clearMessages])

  // If there's context information, automatically send the first message
  useEffect(() => {
    if (context && wheelResult && messages.length === 0 && !isLoading) {
      const initialMessage = `Wheel result: ${wheelResult.text}\n\nProblem background: ${context}\n\nPlease help me analyze this decision and provide advice.`
      sendMessage(initialMessage)
    }
  }, [context, wheelResult, messages.length, isLoading])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = () => {
    if (inputValue.trim() && !isLoading) {
      // Check user login status and usage count
      if (!user && !canUseAI()) {
        setShowLoginPrompt(true)
        return
      }

      // Increase usage count for non-logged-in users
      if (!user) {
        incrementUsage()
      }

      sendMessage(inputValue.trim())
      setInputValue('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handlePresetQuestion = (question: string) => {
    // Check user login status and usage count
    if (!user && !canUseAI()) {
      setShowLoginPrompt(true)
      return
    }

    // Increase usage count for non-logged-in users
    if (!user) {
      incrementUsage()
    }

    let fullQuestion = question
    if (context && wheelResult) {
      fullQuestion = `Wheel result: ${wheelResult.text}\n\nProblem background: ${context}\n\n${question}`
    }
    sendMessage(fullQuestion)
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/"
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Wheel</span>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">AI Decision Analysis</h1>
      </div>

      <div className="card h-[600px] flex flex-col">
        {/* Chat messages area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Start AI Decision Analysis
              </h3>
              <p className="text-gray-600 mb-6">
                {wheelResult ? `Analyzing decision: ${wheelResult.text}` : 'Please use the decision wheel first to get a result for AI analysis'}
              </p>
              
              {!wheelResult && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-yellow-800 font-medium">No decision result available</span>
                  </div>
                  <p className="text-yellow-700 text-sm mt-2">
                    You need to complete the decision wheel first. Go back to the wheel page and spin to get a result.
                  </p>
                  <Link
                    to="/"
                    className="inline-flex items-center mt-3 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Go to Decision Wheel
                  </Link>
                </div>
              )}
              
              {context && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <h4 className="font-medium text-blue-900 mb-2">Decision Context:</h4>
                  <p className="text-blue-800 text-sm whitespace-pre-wrap">{context}</p>
                </div>
              )}
              
              {/* Preset questions */}
              {wheelResult && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                  {presetQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handlePresetQuestion(question)}
                      className="text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`flex items-start space-x-3 max-w-[80%] ${
                    message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="w-4 h-4" />
                    ) : (
                      <Bot className="w-4 h-4" />
                    )}
                  </div>
                  <div
                    className={`p-3 rounded-lg ${
                      message.role === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-3">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={wheelResult ? "Enter your question..." : "Complete the decision wheel first..."}
              className="input-field flex-1 resize-none"
              rows={1}
              disabled={isLoading || !wheelResult}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading || !wheelResult}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-4"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        remainingUsage={getRemainingUsage()}
      />
    </div>
  )
} 